import {InjectionToken, Injector, signal, Signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Editor, JSONContent, NodePos} from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import Color from '@tiptap/extension-color'
import {distinctUntilChanged, Observable, startWith, Subject} from "rxjs";
import {map} from "rxjs/operators";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {erTomEllerNil, trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {Fontstørrelse} from "./utvidelser/Fontstørrelse";
import {TekstBakgrunnsfarge} from "./utvidelser/TekstBakgrunnsfarge";
import {Indentering} from "./utvidelser/Indentering";
import {Underline} from "@tiptap/extension-underline";
import {HttpsUrl} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TabellcelleBakgrunn} from "./utvidelser/TabellcelleBakgrunn";
import {Lenke} from "./utvidelser/Lenke";
import {TextAlign} from "@tiptap/extension-text-align";
import {Sideskift} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/Sideskift";
import OfficePaste from "@intevation/tiptap-extension-office-paste";
import {TabellCelle} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellCelle";
import {Tabell} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/Tabell";
import {erTabellcellerMarkert, erTekstMarkert} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/tiptapHjelpere";
import {TabellcelleRamme} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleRamme";
import {Lenkeinformasjon} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/lenke-rediger/lenke-rediger";
import {InterneLenker} from "felles-ng2/tjenester/InterneLenker";
import {TabellcelleMarg} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleMarger";

const debug = initDebugger('TipTapEditor');

type Tekstmarkering = {
    type: 'text';
    anchor: number;
    head: number;
}

export class TipTapEditor {

    readonly editor: ReturnType<typeof this.opprettEditor>;

    private readonly innholdEndret = new Subject<string>();
    private readonly tilstandEndret = new Subject<void>();
    private readonly harFokusSomSignal = signal(false);
    public readonly aktivLenke: Signal<Lenkeinformasjon | null>;
    /** Sann når tiptap editor er initialisert og klar til bruk */
    public readonly editorInitialisert = signal(false);

    constructor(private interneLenker: InterneLenker, private injector: Injector) {
        this.editor = this.opprettEditor();

        this.aktivLenke = toSignal(this.vedTilstandEndret(() => this.erAktiv(MarkType.Link) ? this.lenkeinformasjon() : null), {initialValue: null});
    }

    /**** generelle formateringskommandoer  ****/

    fjernFormatering(): void {
        this.editor.chain()
          .focus()
          .resetAttributes(NodeType.TableCell, ['style', 'class', 'ramme', 'bakgrunnsfarge'])
          .resetAttributes(NodeType.Table, ['style', 'class'])
          .unsetAllMarks()
          .clearNodes()
          .run();
    }

    /**** tekstformatering  ****/

    toggleTekstjustering(justering: 'left' | 'center' | 'right') {
        if (this.editor.isActive({textAlign: justering}))
            this.editor.chain().focus().unsetTextAlign().run();
        else
            this.editor.chain().focus().setTextAlign(justering).run();
    }

    /**** lenker ****/

    /** sann dersom markør er i en a-tag (node med Link-mark),
     * eller Link-mark er satt som 'stored mark' (feks når ingen tekst er markert og {@link #lagNyLenkeMedTomHref()} har blitt kalt) */
    erLinkTag(): boolean {
        return this.erAktiv(MarkType.Link);
    }

    /** sann dersom aktiv node har Link-mark satt */
    harAktivNodeLinkMark() {
        return this.aktivNodeHarMark(MarkType.Link);
    }

    lenkeAdresse(): HttpsUrl | null {
        return trimTilNull(this.editor.getAttributes(MarkType.Link).href);
    }

    /** Returnerer lenketekst for aktiv node.
     * Dersom markør står i lenke, returneres eksisterende lenkes lenketekst
     * Dersom markør står i tekst som ikke har noe Link mark tilknyttet, returneres null
     * Dersom bruker har markert et tekstfragment returneres teksten i dette fragmentet */
    lenkeTekst(): string | null {
        if (this.editor.state.selection.empty && !this.aktivNodeHarMark(MarkType.Link)) return null;
        const aktivNode = this.editor.state.doc.nodeAt(this.editor.state.selection.from);
        return trimTilNull(aktivNode?.textContent);
    }

    lenkeinformasjon(): Lenkeinformasjon {
        return {
            adresse: this.lenkeAdresse(),
            lenketekst: this.lenkeTekst(),
            attributter: this.editor.getAttributes(MarkType.Link)
        }
    }

    /**** tabeller ****/

    opprettTabell({rows, cols}: { cols: number; rows: number }): void {
        this.editor.chain().focus().insertTable({cols, rows, withHeaderRow: false}).run();
    }

    /** Sann dersom ett eller flere tegn i det tekstlige innholdet er valgt/markert */
    erTekstMarkert(): boolean {
        return erTekstMarkert(this.editor);
    }

    /** Sann dersom en eller flere tabellceller er valgt/markert */
    erTabellcellerMarkert(): boolean {
        return erTabellcellerMarkert(this.editor);
    }

    /**** farger ****/

    lesBakgrunnsfarge() {
        return this.editor.getAttributes(MarkType.TextStyle).tekstbakgrunnsfarge;
    }

    setBakgrunnsfarge(farge: any) {
        if (erTomEllerNil(farge)) this.editor.chain().focus().fjernTekstBakgrunnsfarge().run()
        else this.editor.chain().focus().setTekstBakgrunnsfarge(farge).run();
    }


    lesTekstfarge() {
        return this.editor.getAttributes(MarkType.TextStyle).color;
    }

    setTekstfarge(farge: any) {
        if (erTomEllerNil(farge)) this.editor.chain().focus().unsetColor().run()
        else this.editor.chain().focus().setColor(farge).run();
    }

    /**** state-relatert ****/

    /** Sann dersom markør er i editor-feltet */
    get harFokus(): Signal<boolean> {
        return this.harFokusSomSignal;
    }

    /** sann dersom angitt node eller mark er aktiv, eventuelt med angitte attributter */
    erAktiv(type: string | MarkType | NodeType, attributter?: {}): boolean
    erAktiv(attributter: {}): boolean
    erAktiv(typeEllerAttributter: string, attributter?: {}): boolean {
        return this.editor.isActive(typeEllerAttributter, attributter) ?? false;
    }

    erAktivSomObserverbar(navn: string, attributter?: {}): Observable<boolean>
    erAktivSomObserverbar(attributter: {}): Observable<boolean>
    erAktivSomObserverbar(navnEllerAttributter: string, attributter?: {}): Observable<boolean> {
        return this.tilstandEndret.pipe(
          startWith(null),
          map(() => this.erAktiv(navnEllerAttributter, attributter)),
          distinctUntilChanged(),
        );
    }

    /** sann dersom aktiv node (noden som markør er i) har angitt mark */
    aktivNodeHarMark(type: MarkType) {
        //merk at this.aktivNodePos().node (this.editor.$pos(this.editor.state.selection.anchor)) gir et annet resultat enn uttrykket under,
        //dersom selection.anchor peker inn i en text-node returnerer aktivNodePos() omkringliggende paragraph-node
        const aktivNode = this.editor.state.doc.nodeAt(this.editor.state.selection.from);
        return aktivNode?.marks
          .some(mark => mark.type.name === type) ?? false;
    }

    vedTilstandEndret<T>(mapFunksjon: () => T): Observable<T> {
        return this.tilstandEndret.pipe(
          map(mapFunksjon),
          distinctUntilChanged(),
          takeUntilDestroyed()
        );
    }

    /**** støttefunksjoner ****/

    giFokus() {
        this.editor.commands.focus();
    }

    setDescribedByIds(ids: string[]) {
        this.editor.view.dom.setAttribute('aria-describedby', ids.join(' '))
    }

    setLabeledById(id: string) {
        this.editor.view.dom.setAttribute('aria-labelledby', id)
    }

    /** Returnerer en tiptap {@link NodePos} som representerer markørens posisjon i dokumentet, eller starten på markert innhold dersom bruker har markert innhold */
    aktivNodePos(): NodePos {
        return this.editor.$pos(this.editor.state.selection.anchor);
    }

    /**** livssyklus ****/

    terminer() {
        debug('terminer');
        this.editor.destroy();
    }


    // private hjelpemetoder

    private opprettEditor() {
        const editor = new Editor({extensions: this.tiptapUtvidelser()}); //ved behov for å sette attributter på div-elementet som inneholder det redigerbare innholdet kan editorProps:{attributes:{'et-attributt':'en-verdi'}} legges til her
        editor.on('transaction', () => this.vedOppdatert());
        editor.on('update', () => this.vedOppdatert());
        editor.on('focus', () => this.vedFokusMottatt());
        editor.on('blur', () => this.vedFokusMistet());
        editor.on('create', () => this.editorInitialisert.set(true));

        // @ts-ignore
        window['editor'] = editor; //for å kunne leke med editor i console

        return editor;
    }

    /**
     * Se også https://github.com/ueberdosis/awesome-tiptap#community-extensions for tilgjengelige utvidelser */
    private tiptapUtvidelser() {
        const utvidelser = [
            StarterKit.configure({
                code: false,
                codeBlock: false
                // document: false, //dersom CustomDocument
            }),
            Underline,
            TextAlign.configure({types: ['heading', 'paragraph']}),
            TextStyle, //kreves av FontFamily, Color, FontStørrelseTiptapUtvidelse
            FontFamily,
            Color,
            Tabell().configure({resizable: true, cellMinWidth: 20}),
            TableRow,
            TableHeader,
            TabellCelle(this.injector),
            TabellcelleBakgrunn,
            TabellcelleRamme,
            TabellcelleMarg,
            Sideskift(this.injector),
            Fontstørrelse,
            TekstBakgrunnsfarge,
            Indentering,
            Lenke(this.interneLenker),
            OfficePaste, //vasker innliming fra office, se https://github.com/Intevation/tiptap-extension-office-paste
            /*CustomDocument,
            PersistentHeader,
            PersistentBody,
            Placeholder.configure({
                placeholder: ({node, editor, pos}) => {
                    //tittelblokk kan ikke inneholde barne-noder - kun inline innhold
                    if (node.type.name === 'persistentHeader') return 'Skriv tittel..'

                    //forventer at kropp er 2. barn i dokumentet, og at kropp kun har ett barn
                    if (editor.state.doc.children.length <= 1 ) return '';
                    if(editor.state.doc.children[1].type.name != 'persistentBody' &&
                      editor.state.doc.children[1].children.length != 1) return ''
                    //dersom aktuell node er eneste barn av persistentBody og ikke inneholder noen tekst, da viser vi placeholder
                    if (editor.state.doc.children[1].children[0] == node &&
                      node.type.name === 'paragraph' &&
                      node.textContent === '') return 'Skriv beskrivelse..'

                    return ''
                },
                showOnlyCurrent: false,
                showOnlyWhenEditable: true,
                includeChildren: true,
            }),*/
        ];
        return utvidelser;
    }

    private vedFokusMottatt() {
        this.harFokusSomSignal.set(true);
        //  this.stateChanges.next();
    }

    private vedFokusMistet() {
        //   if (erIkkeTomStreng(this.skjemakontroll().value)) return;
        this.harFokusSomSignal.set(false);
        // this.stateChanges.next();
    }

    private vedOppdatert() {
        const content = this.editor.getHTML();
        const json = this.editor.getJSON().content;
        const value: string = erTomTiptapParagraph(json) ? '' : content;

        this.innholdEndret.next(value);
        this.tilstandEndret.next();
    }
}

function erTomTiptapParagraph(json?: JSONContent[]): boolean {
    return (
      Array.isArray(json) &&
      json.length === 1 &&
      json[0]?.type === 'paragraph' &&
      !json[0]?.hasOwnProperty('content')
    );
}

/** Navn på nodetyper tilgjengelig i tiptap. */
export enum NodeType {
    Paragraph = 'paragraph',
    Heading = 'heading',
    Table = 'table',
    TableRow = 'tableRow',
    TableCell = 'tableCell',
    TableHeader = 'tableHeader',
    Bold = 'bold',
    Italic = 'italic',
    Underline = 'underline',
    //nodetyper vi selv har definert
    SideSkift = 'sideSkift'
}

/** Navn på marks tilgjengelig i tiptap. */
export enum MarkType {
    // TextColor = 'textColor',
    // TextBackgroundColor = 'textBackgroundColor',
    // FontFamily = 'fontFamily',
    // FontSize = 'fontSize',
    Link = 'link',
    TextStyle = 'textStyle'
}

/**  InjectionToken for injisering av {@link TipTapEditor}. */
export const TipTapEditor_DI_TOKEN = new InjectionToken<TipTapEditor>('TipTapEditor implementasjon');
